import { type FC, useMemo } from "react";

import { Colors, Spacing } from "@snapchat/snap-design-system";
import { BarChart, Grid, Threshold, Tooltip, XAxis, YAxis } from "@snapchat/snap-design-system-charts";
// eslint-disable-next-line no-restricted-imports -- SDS-C needs to export Bar from their root barrel
import Bar from "@snapchat/snap-design-system-charts/es/components/Bar";

import { formatNumbers } from "../../../lens-stats/formatters";
import { convertChartLabelNumber } from "../../converters";
import { formatTooltipContent, formatTooltipTitle } from "../../formatters";
import { ChartDimensions, type InsightsMetrics, type StatsItem } from "../../models";

import { chartContainer } from "./charts.styles";

interface EventChartComponent {
    data: InsightsMetrics<StatsItem> | undefined;
    totalPlays?: InsightsMetrics<StatsItem>;
    compareWithTotalPlays?: boolean;
    allowDecimals?: boolean;
}

const KEY_TOTAL_PLAYS = "totalPlays";

export const EventChartComponent: FC<EventChartComponent> = ({
    data,
    totalPlays,
    compareWithTotalPlays,
    allowDecimals,
}) => {
    const formatContent = useMemo(() => formatTooltipContent("activations", formatNumbers), []);

    const mergedStats = useMemo(() => {
        return (
            data?.stats.map((stat, i) => ({
                ...stat,
                value: data.stats[i]?.value,
                [KEY_TOTAL_PLAYS]: totalPlays?.stats[i]?.value ?? 0,
            })) ?? []
        );
    }, [data, totalPlays]);

    return (
        <div className={chartContainer}>
            <BarChart
                data={mergedStats}
                width={1040}
                height={250}
                x={ChartDimensions.key}
                margin={{ left: Spacing["2XS"], right: Spacing["2XS"] }}
            >
                <Grid strokeDasharray="1 6" />
                <XAxis labelOrientation="vertical" interval={0} />
                <YAxis label={convertChartLabelNumber} allowDecimals={allowDecimals} />
                <Tooltip title={formatTooltipTitle} content={formatContent} />
                {data && <Threshold value={data.averageValue} />}
                <Bar y={ChartDimensions.value} strokeColor={Colors.Black.V50} activeStrokeColor={Colors.Yellow.V200} />
                {totalPlays && compareWithTotalPlays && (
                    <Bar
                        y={KEY_TOTAL_PLAYS}
                        stackId={KEY_TOTAL_PLAYS}
                        strokeColor={Colors.Gray.V250}
                        activeStrokeColor={Colors.Yellow.V200}
                    />
                )}
            </BarChart>
        </div>
    );
};
